import React, { useState, useEffect, useRef } from "react";
import logo from "assets/images/ebullion-logo.svg";
import { Link, useLocation, useHistory } from "react-router-dom";
import { userStore } from "../../store/UserStore";
import { clearSessionData } from "utils";
import TopBands from "./TopBands";
import ArticleView from "pages/articles";
import { resetKycCache } from '../../routes/FraudRoute';
import "./style.css";
import Referral from "pages/referral/Referral";

interface HeaderProps {
  isLinkShow: boolean;
}

interface NavLinkItem {
  label: string;
  path: string;
  showFor?: "REGISTERED" | "UNREGISTERED" | "ALL";
  isFeature?: boolean;
}

const Header: React.FC<HeaderProps> = ({ isLinkShow }) => {
  const location = useLocation();
  const history = useHistory();
  const [user, customerId, customer] = userStore((state: any) => [
    state.user,
    state.customerId,
    state.customer,
  ]);

  const [showArticle, setShowArticle] = useState(false);
  const [showRefer, setShowRefer] = useState(false);
  const [currentPath, setCurrentPath] = useState(location.pathname);
  const [menuOpen, setMenuOpen] = useState(false);
  const [chartsOpen, setChartsOpen] = useState(false);
  const [txnOpen, setTxnOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [mobileUserOpen, setMobileUserOpen] = useState(false);

  const chartsRef = useRef<HTMLDivElement>(null);
  const txnRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentPath(location.pathname);
    setMenuOpen(false);
  }, [location]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (chartsRef.current && !chartsRef.current.contains(e.target as Node)) setChartsOpen(false);
      if (txnRef.current && !txnRef.current.contains(e.target as Node)) setTxnOpen(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const signout = () => {
    clearSessionData();
    resetKycCache();
    history.push("/signin");
  };

  const goToExternal = (url: string) => window.open(url, "_blank");
  const isActive = (path: string) => currentPath === path;

  const navLinks: NavLinkItem[] = [
    { label: "Dashboard",       path: "/dashboard",      showFor: "ALL" },
    { label: "Buy / Sell",      path: "/buy-sell",       showFor: "ALL" },
    { label: "SIP",             path: "/sip",            showFor: "ALL" },
    { label: "Daily Savings",   path: "/daily-saving",   showFor: "ALL" },
    { label: "Multimetal SIP",  path: "/multimetal-sip", showFor: "UNREGISTERED", isFeature: true },
    { label: "Buy Coins & Bars",path: "/buy-coins-bars", showFor: "UNREGISTERED" },
    { label: "Passbook",        path: "/passbook",       showFor: "REGISTERED" },
  ];

  const filteredLinks = navLinks.filter((link) => {
    if (link.showFor === "ALL") return true;
    return link.showFor === customer?.customerCategory;
  });

  const ChevronIcon = ({ open }: { open: boolean }) => (
    <svg
      width="10" height="10" viewBox="0 0 10 10" fill="none"
      className={`eb-chevron ${open ? "eb-chevron--open" : ""}`}
    >
      <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const avatarLetter = (user?.displayName || "U")[0].toUpperCase();

  return (
    <>
      <TopBands />
      <header className="eb-header">
        <div className="eb-header__inner">

          {/* ── Logo ── */}
          <Link to="/dashboard" className="eb-header__logo">
            <img src={logo} alt="eBullion" />
          </Link>

          {/* ── Desktop + Mobile Nav ── */}
          <nav className={`eb-nav ${menuOpen ? "eb-nav--open" : ""}`}>

            {/* Regular nav links */}
            {isLinkShow && filteredLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`eb-nav__link${isActive(link.path) ? " eb-nav__link--active" : ""}${link.isFeature ? " eb-nav__link--feature" : ""}`}
              >
                {link.isFeature && <span className="eb-nav__feature-ring" />}
                {link.label}
              </Link>
            ))}

            {/* Transactions dropdown — UNREGISTERED only */}
            {customer?.customerCategory === "UNREGISTERED" && (
              <div className="eb-dropdown" ref={txnRef}>
                <button
                  className={`eb-nav__link eb-dropdown__trigger${txnOpen ? " eb-nav__link--active" : ""}`}
                  onClick={() => setTxnOpen((p) => !p)}
                >
                  Transactions <ChevronIcon open={txnOpen} />
                </button>
                {txnOpen && (
                  <div className="eb-dropdown__menu">
                    <Link
                      to="/passbook"
                      className={`eb-dropdown__item${isActive("/passbook") ? " eb-dropdown__item--active" : ""}`}
                      onClick={() => setTxnOpen(false)}
                    >
                      Passbook
                    </Link>
                    <Link
                      to="/delivery-orders"
                      className={`eb-dropdown__item${isActive("/delivery-orders") ? " eb-dropdown__item--active" : ""}`}
                      onClick={() => setTxnOpen(false)}
                    >
                      Delivery Orders
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Charts dropdown */}
            <div className="eb-dropdown" ref={chartsRef}>
              <button
                className={`eb-nav__link eb-dropdown__trigger${chartsOpen ? " eb-nav__link--active" : ""}`}
                onClick={() => setChartsOpen((p) => !p)}
              >
                Charts <ChevronIcon open={chartsOpen} />
              </button>
              {chartsOpen && (
                <div className="eb-dropdown__menu">
                  <Link
                    to="/price-charts"
                    className={`eb-dropdown__item${isActive("/price-charts") ? " eb-dropdown__item--active" : ""}`}
                    onClick={() => setChartsOpen(false)}
                  >
                    Price Charts
                  </Link>
                  <Link
                    to="/gold-silver"
                    className={`eb-dropdown__item${isActive("/gold-silver") ? " eb-dropdown__item--active" : ""}`}
                    onClick={() => setChartsOpen(false)}
                  >
                    Gold / Silver Ratio
                  </Link>
                </div>
              )}
            </div>

            {/* ── Mobile-only: User section (appears inline in nav) ── */}
            <div className="eb-nav__mobile-user-section">
              <div className="eb-nav__mobile-divider" />

              <button
                className="eb-nav__link eb-nav__mobile-user-toggle"
                onClick={() => setMobileUserOpen((p) => !p)}
              >
                <span className="eb-nav__mobile-avatar">{avatarLetter}</span>
                <span className="eb-nav__mobile-username">{user?.displayName || "Account"}</span>
                <ChevronIcon open={mobileUserOpen} />
              </button>

              {mobileUserOpen && (
                <div className="eb-nav__mobile-user-menu">
                  <button
                    className="eb-nav__link eb-nav__mobile-user-item"
                    onClick={() => { setShowArticle(true); setMenuOpen(false); setMobileUserOpen(false); }}
                  >
                    Articles
                  </button>
                  <button
                    className="eb-nav__link eb-nav__mobile-user-item"
                    onClick={() => goToExternal("https://ebullion.in/terms-and-conditions/privacypolicy.html")}
                  >
                    Privacy Policy
                  </button>
                  <button
                    className="eb-nav__link eb-nav__mobile-user-item"
                    onClick={() => goToExternal("https://ebullion.in/terms-and-conditions/terms.html")}
                  >
                    Terms &amp; Conditions
                  </button>
                  <button
                    className="eb-nav__link eb-nav__mobile-user-item"
                    onClick={() => { setShowRefer(true); setMenuOpen(false); setMobileUserOpen(false); }}
                  >
                    Refer a Friend
                  </button>
                  {customerId && (
                    <button
                      className="eb-nav__link eb-nav__mobile-user-item"
                      onClick={() => { history.push("/profile"); setMenuOpen(false); setMobileUserOpen(false); }}
                    >
                      My Profile
                    </button>
                  )}
                  <div className="eb-nav__mobile-divider" />
                  <button
                    className="eb-nav__link eb-nav__mobile-user-item eb-nav__mobile-user-item--danger"
                    onClick={signout}
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
            {/* ── END Mobile User Section ── */}

          </nav>

          {/* ── Right side: Desktop user dropdown + Hamburger ── */}
          <div className="eb-header__right">

            {/* Desktop User dropdown */}
            <div className="eb-dropdown eb-dropdown--user" ref={userRef}>
              <button className="eb-user-btn" onClick={() => setUserOpen((p) => !p)}>
                <span className="eb-user-btn__avatar">{avatarLetter}</span>
                <span className="eb-user-btn__name">{user?.displayName || ""}</span>
                <ChevronIcon open={userOpen} />
              </button>
              {userOpen && (
                <div className="eb-dropdown__menu eb-dropdown__menu--right">
                  <button className="eb-dropdown__item" onClick={() => { setShowArticle(true); setUserOpen(false); }}>
                    Articles
                  </button>
                  <button className="eb-dropdown__item" onClick={() => goToExternal("https://ebullion.in/terms-and-conditions/privacypolicy.html")}>
                    Privacy Policy
                  </button>
                  <button className="eb-dropdown__item" onClick={() => goToExternal("https://ebullion.in/terms-and-conditions/terms.html")}>
                    T&amp;C
                  </button>
                  <button className="eb-dropdown__item" onClick={() => { setShowRefer(true); setUserOpen(false); }}>
                    Refer a Friend
                  </button>
                  {customerId && <div className="eb-dropdown__divider" />}
                  {customerId && (
                    <button className="eb-dropdown__item" onClick={() => { history.push("/profile"); setUserOpen(false); }}>
                      My Profile
                    </button>
                  )}
                  <div className="eb-dropdown__divider" />
                  <button className="eb-dropdown__item eb-dropdown__item--danger" onClick={signout}>
                    Sign Out
                  </button>
                </div>
              )}
            </div>

            {/* Hamburger */}
            <button
              className={`eb-hamburger${menuOpen ? " eb-hamburger--open" : ""}`}
              onClick={() => setMenuOpen((p) => !p)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              <span className="eb-hamburger__bar" />
              <span className="eb-hamburger__bar" />
              <span className="eb-hamburger__bar" />
            </button>
          </div>

        </div>
      </header>

      <ArticleView handleModal={() => setShowArticle(false)} isShow={showArticle} />
      <Referral handleModal={() => setShowRefer(false)} isShow={showRefer} />
    </>
  );
};

export default Header;